/**
 * Alpine.js City Editor Component
 * Provides inline editing functionality for city selection with autocomplete
 */

import { editorBase } from './alpine-editor-base.js';
import { formatDurationShort } from './time-utils.js';
import { SM_BREAKPOINT } from './breakpoints.js';
import { searchCities } from './city-search.js';

/** Milliseconds since the epoch for an ISO 8601 value, or null for an empty or invalid one. */
export function parseTimestamp(value) {
    if (!value) return null;
    const time = Date.parse(value);
    return Number.isNaN(time) ? null : time;
}

/**
 * The availability after this editor saves the current city. A save inside the signup grace and a
 * moderator save arm no cooldown.
 */
export function nextAvailableAt(editor, now) {
    if (editor.fieldName !== 'city_id' || !editor.cooldownApplies) return editor.availableAt;
    if (editor.graceEndsAt !== null && editor.graceEndsAt > now) return null;
    return now + editor.geoCooldown * 1000;
}

/**
 * City Editor Component Factory
 * Handles city_id and home_city_id fields with autocomplete search
 */
export function cityEditor() {
    return {
        // State
        isEditing: false,
        searchQuery: '',
        cityId: '',
        cityName: '',
        countryName: '',
        countryIso: '',
        originalCityId: '',
        originalCityName: '',
        originalCountryName: '',
        originalCountryIso: '',
        isSaving: false,
        isSearching: false,
        error: null,
        searchResults: [],
        showDropdown: false,
        highlightedIndex: -1,
        searchTimeout: null,

        // Configuration (set from data attributes or defaults)
        fieldName: '',
        endpoint: '',

        // Cooldown state. The server computes availableAt from the location state (docs#10).
        availableAt: null,
        graceEndsAt: null,
        cooldownApplies: false,
        geoCooldown: 0,
        cooldownNoticeText: '',
        cooldownAvailableInText: '',

        // Computed properties
        get hasChanges() {
            return this.cityId !== this.originalCityId;
        },

        get cooldownBlocked() {
            return this.availableAt !== null && this.availableAt > Date.now();
        },

        get cooldownMessage() {
            if (!this.cooldownBlocked) return '';
            const timeStr = formatDurationShort((this.availableAt - Date.now()) / 1000);
            return this.cooldownAvailableInText
                ? this.cooldownAvailableInText.replace('%time%', timeStr)
                : `You can change your location again in ${timeStr}`;
        },

        get displayValue() {
            if (!this.cityName) {
                return '';
            }
            return `${this.cityName}, ${this.countryName}`;
        },

        get flagUrl() {
            if (!this.countryIso) return '';
            return `/images/circle-flags/flags/${this.countryIso.toLowerCase()}.svg`;
        },

        // Methods
        init() {
            const element = this.$el;

            this.fieldName = element.dataset.field || this.fieldName;
            this.endpoint = element.dataset.endpoint || '/v1/account/self';

            // Get initial values from element's data attributes
            this.originalCityId = element.dataset.cityId || '';
            this.originalCityName = element.dataset.cityName || '';
            this.originalCountryName = element.dataset.countryName || '';
            this.originalCountryIso = element.dataset.countryIso || '';

            this.cityId = this.originalCityId;
            this.cityName = this.originalCityName;
            this.countryName = this.originalCountryName;
            this.countryIso = this.originalCountryIso;

            // Cooldown data
            this.availableAt = parseTimestamp(element.dataset.locationChangeAvailableAt);
            this.graceEndsAt = parseTimestamp(element.dataset.graceEndsAt);
            this.cooldownApplies = element.dataset.cooldownApplies === '1';
            this.geoCooldown = parseInt(element.dataset.geoCooldown, 10) || 0;
            this.cooldownNoticeText = element.dataset.cooldownNotice || '';
            this.cooldownAvailableInText = element.dataset.cooldownAvailableIn || '';

            // Initialize base utilities
            editorBase.initEditor(this);
        },

        getSearchInput() {
            if (window.innerWidth < SM_BREAKPOINT && this.$refs.searchInputMobile) {
                return this.$refs.searchInputMobile;
            }
            return this.$refs.searchInput;
        },

        clearSearch() {
            this.searchQuery = '';
            this.cityId = '';
            this.cityName = '';
            this.countryName = '';
            this.countryIso = '';
            this.searchResults = [];
            this.showDropdown = false;
            this.error = null;
            this.$nextTick(() => {
                this.getSearchInput()?.focus();
            });
        },

        startEdit() {
            this.isEditing = true;
            this.error = null;
            this.searchQuery = this.displayValue;
            this.searchResults = [];
            this.showDropdown = false;
            this.highlightedIndex = -1;

            if (window.innerWidth < SM_BREAKPOINT) {
                window.scrollLock?.lock();
            }

            // Register with store for tracking
            if (this.$store.profile) {
                this.$store.profile.startEditing(this.fieldName);
            }

            // Focus the input and initialize icons after Alpine updates the DOM
            this.$nextTick(() => {
                window.LucideIcons?.initialize(this.$el);
                const input = this.getSearchInput();
                if (input) {
                    input.focus();
                    input.select();
                }
            });
        },

        cancel() {
            this.cityId = this.originalCityId;
            this.cityName = this.originalCityName;
            this.countryName = this.originalCountryName;
            this.countryIso = this.originalCountryIso;
            this.searchQuery = '';
            this.searchResults = [];
            this.showDropdown = false;
            this.error = null;
            this.completeEdit();
        },

        completeEdit() {
            this.isEditing = false;
            this.showDropdown = false;
            window.scrollLock?.unlock();

            // Unregister from store
            if (this.$store.profile) {
                this.$store.profile.stopEditing(this.fieldName);
            }
        },

        async search() {
            const query = this.searchQuery.trim();

            if (query.length < 2) {
                this.searchResults = [];
                this.showDropdown = false;
                return;
            }

            // Debounce the search
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }

            this.searchTimeout = setTimeout(async () => {
                this.isSearching = true;

                try {
                    this.searchResults = await searchCities(query);
                    this.showDropdown = this.searchResults.length > 0;
                    this.highlightedIndex = -1;
                } catch (error) {
                    console.error('City search error:', error);
                    this.searchResults = [];
                    this.showDropdown = false;
                    this.highlightedIndex = -1;
                } finally {
                    this.isSearching = false;
                }
            }, 300);
        },

        selectCity(city) {
            this.cityId = city.id;
            this.cityName = city.name;
            this.countryName = city.countryName;
            this.countryIso = city.countryIso;
            this.searchQuery = `${city.name}, ${city.countryName}`;
            this.showDropdown = false;
            this.searchResults = [];
        },

        formatCityDisplay(city) {
            let display = city.name;
            if (city.regionName) {
                display += `, ${city.regionName}`;
            }
            if (city.countryName) {
                display += `, ${city.countryName}`;
            }
            return display;
        },

        handleKeydown(event) {
            if (event.key === 'Escape') {
                if (this.showDropdown) {
                    this.showDropdown = false;
                } else {
                    this.cancel();
                }
            } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                if (this.showDropdown && this.searchResults.length > 0) {
                    this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.searchResults.length - 1);
                }
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                if (this.showDropdown && this.searchResults.length > 0) {
                    this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
                }
            } else if (event.key === 'Enter') {
                event.preventDefault();
                if (this.showDropdown && this.highlightedIndex >= 0 && this.searchResults[this.highlightedIndex]) {
                    this.selectCity(this.searchResults[this.highlightedIndex]);
                }
            }
        },

        handleBlur() {
            // Delay hiding dropdown to allow click selection
            setTimeout(() => {
                this.showDropdown = false;
            }, 200);
        },

        async save() {
            if (!this.hasChanges || this.isSaving) {
                return;
            }

            // Some fields (e.g. home_city_id) can be cleared once previously set.
            // Others (e.g. city_id) always require a value.
            const canBeUnset = this.fieldName === 'home_city_id';
            if (!this.cityId && !(canBeUnset && this.originalCityId)) {
                this.error = window.tOr('common.select_city_from_list', 'Please select a city from the list.');
                return;
            }

            this.isSaving = true;
            this.error = null;

            try {
                // Save to server and wait for response (empty value clears the field server-side)
                const result = await editorBase.saveField(this.endpoint, this.fieldName, this.cityId);

                // Save successful - update local state
                this.originalCityId = this.cityId;
                this.originalCityName = this.cityName;
                this.originalCountryName = this.countryName;
                this.originalCountryIso = this.countryIso;

                this.availableAt = nextAvailableAt(this, Date.now());

                // Update store
                if (this.$store.profile) {
                    this.$store.profile.updateField(this.fieldName, this.cityId);
                }

                // Exit edit mode
                this.completeEdit();

            } catch (error) {
                // Show error and keep in edit mode
                this.error = error.message || 'Failed to save changes. Please try again.';
                console.error('Save error:', error);
            } finally {
                this.isSaving = false;
            }
        }
    }
}
